import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 首屏的迷你看板：用产品里真实的列头、卡片样式做的，不是截图。
 * 一张 agent 的卡片自己从「待办」走到「待验收」，然后停下来等访客：
 *   点 ✓ 通过 → 进「已完成」；点 ← 打回 → 回到「进行中」再做一遍；
 *   点「让 agent 自己点已完成」→ 被拒绝（这就是产品最核心的规则）。
 */

type Stage = 0 | 1 | 2 | 3; // 0 待办  1 进行中  2 待验收  3 已完成

const REDUCED_MOTION =
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** 列头：颜色、圆点、数量标都和产品里的一致 */
const COLS = {
  todo: { title: '待办任务', head: 'border-b border-neutral-100', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' },
  doing: { title: '进行中', head: 'bg-gradient-to-r from-blue-50 to-blue-100 border-b border-neutral-100', dot: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-600' },
  review: { title: '待验收', head: 'bg-gradient-to-r from-amber-50 to-amber-100 border-b border-neutral-100', dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-700' },
  done: { title: '已完成', head: 'bg-gradient-to-r from-green-50 to-green-100 border-b border-neutral-100', dot: 'bg-green-400', badge: 'bg-green-100 text-green-600' },
} as const;

export const ColumnShell: React.FC<{
  tone: keyof typeof COLS | 'blocked';
  title?: string;
  count?: number;
  className?: string;
  children: React.ReactNode;
}> = ({ tone, title, count, className = '', children }) => {
  const c =
    tone === 'blocked'
      ? { title: '受阻', head: 'bg-gradient-to-r from-red-50 to-red-100 border-b border-neutral-100', dot: 'bg-red-400', badge: 'bg-red-100 text-red-600' }
      : COLS[tone];
  return (
    <section className={`flex flex-col rounded-t-xl border border-neutral-100 bg-neutral-25 shadow-sm ${className}`}>
      <header className={`rounded-t-xl px-4 py-3.5 ${c.head}`}>
        <h3 className="flex items-center text-[15px] font-semibold text-gray-700">
          <span className={`mr-2.5 inline-block h-2.5 w-2.5 rounded-full ${c.dot}`} />
          {title ?? c.title}
          {count !== undefined && <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-normal ${c.badge}`}>{count}</span>}
        </h3>
      </header>
      <div className="flex-1 space-y-2 p-3">{children}</div>
    </section>
  );
};

/** 产品里任务卡片的样子：按优先级淡淡的底色，标题、负责人、大小点 */
const BG = { HIGH: 'rgba(254, 226, 226, 0.4)', MEDIUM: 'rgba(254, 243, 199, 0.25)', LOW: 'rgba(219, 234, 254, 0.25)' } as const;

const Card: React.FC<{
  title: string;
  priority: keyof typeof BG;
  owner?: string;
  ai?: boolean;
  done?: boolean;
  className?: string;
  children?: React.ReactNode;
}> = ({ title, priority, owner, ai, done, className = '', children }) => (
  <div
    className={`rounded-lg border border-white/60 px-3 py-2.5 shadow-[0_1px_3px_rgba(0,0,0,0.08)] ${className}`}
    style={{ backgroundColor: done ? 'rgba(241,245,249,0.7)' : BG[priority] }}
  >
    <div className={`text-sm font-medium leading-snug ${done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{title}</div>
    {children}
    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
      <span className="flex items-center gap-1">
        {owner ?? <span className="text-gray-300">未分配</span>}
        {ai && <span className="rounded bg-indigo-100 px-1 text-indigo-700">AI</span>}
      </span>
      <span className="h-2 w-2 rounded-full bg-gray-500" />
    </div>
  </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="rounded-lg border border-dashed border-neutral-150 px-3 py-6 text-center text-xs text-gray-400">{text}</div>
);

const STATUS_TEXT: Record<Stage, string> = {
  0: 'agent 领取任务。这张卡写了完成标准，所以它能领。',
  1: 'agent 拆解、改动，另一个 agent 审查，再真实运行验证。',
  2: '做完了，交到「待验收」。看一份报告，通过或打回。',
  3: '只有你能点通过。这一步，agent 做不到。',
};

export const HeroBoard: React.FC = () => {
  const [stage, setStage] = useState<Stage>(REDUCED_MOTION ? 2 : 0);
  const [rework, setRework] = useState(false);
  const [denied, setDenied] = useState(false);
  const timers = useRef<number[]>([]);
  const deniedTimer = useRef<number>();

  // 自动推进：待办 → 进行中 → 待验收；到待验收就停下，等访客操作
  useEffect(() => {
    if (REDUCED_MOTION) return;
    if (stage === 0) timers.current.push(window.setTimeout(() => setStage(1), 1500));
    if (stage === 1) {
      timers.current.push(window.setTimeout(() => { setRework(false); setStage(2); }, rework ? 2400 : 3200));
    }
    return () => { timers.current.forEach(clearTimeout); timers.current = []; };
  }, [stage, rework]);

  useEffect(() => () => window.clearTimeout(deniedTimer.current), []);

  const approve = useCallback(() => { setDenied(false); setStage(3); }, []);
  const reject = useCallback(() => { setDenied(false); setRework(true); setStage(1); }, []);
  const replay = useCallback(() => { setRework(false); setStage(REDUCED_MOTION ? 2 : 0); }, []);
  const tryAgentDone = useCallback(() => {
    setDenied(true);
    window.clearTimeout(deniedTimer.current);
    deniedTimer.current = window.setTimeout(() => setDenied(false), 3000);
  }, []);

  const hero = (extra?: React.ReactNode, done = false) => (
    <Card key={stage} title="设置页加深色模式" priority="HIGH" owner="Claude" ai done={done} className="l-pop">
      {extra}
    </Card>
  );

  const fillers = [
    <Card key="a" title="任务列表导出为 CSV" priority="MEDIUM" />,
    <Card key="b" title="整理 9 月的客户反馈" priority="LOW" />,
  ];

  return (
    <div>
      {/* 列太窄时在看板内部横向滚动，不折行，和产品里一致 */}
      <div className="-mx-5 overflow-x-auto px-5 pb-3 sm:-mx-8 sm:px-8">
        <div className="grid min-w-[880px] grid-cols-4 gap-4">
          <ColumnShell tone="todo" count={stage === 0 ? 3 : 2} className="min-h-[300px]">
            {stage === 0 && hero()}
            {fillers}
          </ColumnShell>

          <ColumnShell tone="doing" count={stage === 1 ? 1 : 0} className="min-h-[300px]">
            {stage === 1 ? (
              hero(
                <div className="mt-2 text-xs text-gray-500" aria-hidden="true">
                  {rework ? '按你的意见修改' : '拆解、实施中'}
                  <span className="ml-1 inline-flex gap-0.5">
                    <span className="l-dot inline-block h-1 w-1 rounded-full bg-gray-500" />
                    <span className="l-dot inline-block h-1 w-1 rounded-full bg-gray-500" />
                    <span className="l-dot inline-block h-1 w-1 rounded-full bg-gray-500" />
                  </span>
                </div>,
              )
            ) : (
              <Empty text="agent 领取后出现在这里" />
            )}
          </ColumnShell>

          <ColumnShell tone="review" count={stage === 2 ? 1 : 0} className="min-h-[300px]">
            {stage === 2 ? (
              <Card key="review" title="设置页加深色模式" priority="HIGH" owner="Claude" ai className="l-pop">
                <ul className="mt-2 space-y-1 border-l-2 border-amber-400 pl-2 text-xs text-gray-600">
                  <li><b className="font-medium text-gray-700">改了什么</b> 6 个文件</li>
                  <li><b className="font-medium text-gray-700">审查</b> 发现 2 处，已修</li>
                  <li><b className="font-medium text-gray-700">验证</b> 四个宽度无遮挡</li>
                  <li className="text-amber-700"><b className="font-medium">没验到</b> Safari 外观联动</li>
                </ul>
                <div className="mt-2.5 flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={reject}
                    aria-label="打回"
                    title="打回，让 agent 接着改"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400 text-sm text-white hover:bg-amber-500"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={approve}
                    aria-label="通过，标为已完成"
                    title="通过，标为已完成"
                    className="l-ring flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-sm text-white hover:bg-green-600"
                  >
                    ✓
                  </button>
                </div>
              </Card>
            ) : (
              <Empty text="agent 做完交到这里" />
            )}
          </ColumnShell>

          <ColumnShell tone="done" count={stage === 3 ? 2 : 1} className="min-h-[300px]">
            {stage === 3 && hero(undefined, true)}
            <Card title="更新定价页文案" priority="LOW" owner="Claude" ai done />
          </ColumnShell>
        </div>
      </div>

      {/* 说明当前发生了什么 + 可以试的操作 */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[15px] text-gray-600" role="status" aria-live="polite">{STATUS_TEXT[stage]}</p>
        <div className="flex shrink-0 items-center gap-3">
          {stage === 2 && (
            <button
              type="button"
              onClick={tryAgentDone}
              className={`rounded-md border border-gray-300 bg-white px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50 ${denied ? 'l-shake' : ''}`}
            >
              让 agent 自己点已完成
            </button>
          )}
          {stage === 3 && (
            <button type="button" onClick={replay} className="rounded-md border border-gray-300 bg-white px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50">
              再看一遍
            </button>
          )}
        </div>
      </div>
      <p className="mt-4 text-xs text-gray-400">以上是演示，任务和数据都是示例，不是真实运行。</p>
      <div className="mt-2 min-h-[22px] text-sm" role="alert">
        {denied && <span className="font-medium text-red-600">被拒绝：agent 不能把任务标为已完成，接口层面就不允许。</span>}
        {stage === 2 && !denied && <span className="text-gray-400">试试点卡片上的 ✓ 通过，或 ← 打回。</span>}
      </div>
    </div>
  );
};
